/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import { connect } from "react-redux";
import get from "lodash/get";
import { LoadMoreStoriesBase, LoadMoreCollectionStories, Collection } from "@quintype/components";
import PropTypes from "prop-types";
import { getCollectionTemplate } from "../get-collection-template";
import { updateStoriesWithAlternatives, overrideLayouts, addCollectionIndex, normalizeStoryUrl } from "../../utils";
import { overrideLayoutsForSection } from "../../section-page-utils";
import { ThreeColGrid } from "@quintype/arrow";

const SectionPageBase = props => {
  const {
    darkBgColor,
    bgColor,
    enableIntroCard = false
  } = get(props, ["config", "pagebuilder-config", "section", "pageSetting"], {});
  const style = (props.enableDarkMode ? darkBgColor : bgColor) || bgColor;
  const collectionName = get(props, ["data", "collection", "name"], "");
  return (
    <div style={{ backgroundColor: style || "initial" }}>
      {/* H1 is not required visually but need for SEO */}
      {!enableIntroCard && <h1 style={{ display: "none" }}>{collectionName}</h1>}
      <CollectionLayout {...props} />
    </div>
  );
};

export function CollectionLayout (props) {
  const { config, data } = props;
  const collectionData = get(props, ["data", "collection", "items"], []);
  const customCollection = overrideLayoutsForSection(config, data?.collection, data?.customSection);
  const updatedCollectionWithAlternativesData = updateStoriesWithAlternatives(
    collectionData.filter(item => item.type === "story") // Initially collectionData is null which checks for collection to avoid returning undefined.
  );

  const pageConfig = get(config, ["pagebuilder-config", "section"]);
  const layoutDataType = get(pageConfig, ["pageSetting", "layoutDataType"]);
  const collection =
    layoutDataType === undefined ? overrideLayouts(config, data.collection, "section") : customCollection;
  addCollectionIndex(collection)
  return (
    collection &&
    (collection.items.some(collection => collection.type === "collection") ? (
      <Collection config={config} collection={collection} collectionTemplates={getCollectionTemplate} />
    ) : (
      <LoadMoreStories
        {...props}
        stories={updatedCollectionWithAlternativesData}
        languageDirection={get(config, ["language", "direction"], "ltr") === "ltr" ? "left" : "right"}
      />
    ))
  );
}
CollectionLayout.propTypes = {
  config: PropTypes.object,
  data: PropTypes.object
};

function LoadMoreStoriesTemplate (props) {
  const defaultTemplate = () => {
    const getStoryData = get(props, ["stories"], []).map(story => ({ story: normalizeStoryUrl(story), type: "story" }));
    const collection = { type: "collection", items: getStoryData, name: get(props, ["collection", "name"], "") };
    return (
      <div className="component-wrapper">
        <ThreeColGrid collection={collection} isLoadMoreVisible={false} />
      </div>
    );
  };
  return defaultTemplate();
}

export function LoadMoreStories (props) {
  const collection = get(props, ["data", "section", "collection"], null);
  const storyFields =
    "headline,subheadline,summary,sections,tags,author-name,author-id,authors,updated-at,last-published-at,published-at,updated-at,first-published-at,hero-image-metadata,hero-image-s3-key,story-content-id,slug,id,seo,story-template,url";
  const data = Object.assign({}, props.data, { stories: get(props, ["stories"], []) });
  const sectionId = get(props, ["data", "section", "id"], -1);
  const numStoriesToLoad = 10;
  return collection ? (
    <LoadMoreCollectionStories
      template={LoadMoreStoriesTemplate}
      collectionSlug={collection.slug}
      params={{ "item-type": "story" }}
      data={data}
      languageDirection={props.languageDirection}
      numStoriesToLoad={numStoriesToLoad}
    />
  ) : (
    <LoadMoreStoriesBase
      template={LoadMoreStoriesTemplate}
      fields={storyFields}
      params={{ "section-id": sectionId }}
      data={data}
      languageDirection={props.languageDirection}
      numStoriesToLoad={numStoriesToLoad}
    />
  );
}
LoadMoreStories.propTypes = {
  data: PropTypes.object,
  languageDirection: PropTypes.string,
  stories: PropTypes.array
};

function mapStateToProps (state) {
  return {
    config: get(state, ["qt", "config"], {}),
    enableDarkMode: get(state, ["header", "isDarkModeEnabled"], false)
  };
}

export const SectionPage = connect(mapStateToProps)(SectionPageBase);
